/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import AtomicSDK, {
  AACHeadlessStreamContainer,
  HorizontalContainerConfig,
} from '@atomic.io/action-cards-web-sdk';
import React, { useCallback, useEffect } from 'react';
import { ATOMIC_STREAM_DASHBOARD_PROMOTIONS, useAtomic } from './AtomicProvider';
import { selectConfigByKey } from '../firebase/remote-config/remoteConfig.slice';
import { useSelector } from 'react-redux';
import AtomicEmbedHorizontal from './AtomicEmbedHorizontal';
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
const initialCardCount = { cardCount: 0, totalCards: 0 };

export const DashboardPromotionsAtomic = () => {
  const atomicEnabled = useSelector(selectConfigByKey('atomicEnabled'));
  const atomicPromotionsEnabled = useSelector(selectConfigByKey('atomicPromotionsEnabled'));
  const atomic = useAtomic();
  const [{ totalCards }, setPromotionCardCount] = React.useState<{
    cardCount: number;
    totalCards: number;
  }>(initialCardCount);
  const promotionStream = React.useRef<AACHeadlessStreamContainer>();

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('md'));

  const updateCardCount = useCallback(() => {
    AtomicSDK.requestUserMetrics()
      .then((metrics) => {
        const cardCount = metrics.unseenCardsForStreamContainer(ATOMIC_STREAM_DASHBOARD_PROMOTIONS);
        const totalCards = metrics.totalCardsForStreamContainer(ATOMIC_STREAM_DASHBOARD_PROMOTIONS);
        setPromotionCardCount({ cardCount, totalCards });
      })
      .catch((err) => {
        // Supress from sentry
        if (err?.toString().includes('Device is not connected to the internet')) {
          console.log(err);
        } else {
          console.error(err);
        }
      });
  }, [setPromotionCardCount]);

  useEffect(() => {
    if (atomic.atomicReady) {
      // Initialise and Observe inbox changes in headless mode
      updateCardCount();
      promotionStream.current = AtomicSDK.observableStreamContainer({
        streamContainerId: ATOMIC_STREAM_DASHBOARD_PROMOTIONS,
        cardListRefreshInterval: 10,
        features: {
          runtimeVariableAnalytics: false,
        },
        onCardCountChanged() {
          updateCardCount();
          return null;
        },
        cardFeedHandler() {
          return null;
        },
      });
    }
  }, [atomic.atomicReady, updateCardCount]);

  const configMemo = React.useMemo(() => {
    return {
      enabled: true,
      cardWidth: isSm ? (window.innerWidth / 100) * 60 : 354,
      emptyStyle: 'standard',
      headerAlignment: 'left',
      scrollMode: 'snap',
      lastCardAlignment: 'left',
    } as HorizontalContainerConfig;
  }, [isSm]);

  if (atomicPromotionsEnabled && atomicEnabled && atomic.atomicReady) {
    return (
      <Stack
        width='100%'
        sx={{
          ...(totalCards <= 0 ? { display: 'none' } : { display: 'flex' }),
        }}
      >
        <Typography variant='h4'>Promotions</Typography>
        <Box
          sx={{
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
            gap: '16px',
          }}
        >
          <AtomicEmbedHorizontal
            onChange={({ cardCount, totalCards }) => {
              setPromotionCardCount({ cardCount, totalCards });
            }}
            containerConfig={configMemo}
            containerId={ATOMIC_STREAM_DASHBOARD_PROMOTIONS}
          />
        </Box>
      </Stack>
    );
  }
};
